// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file <INSERT_FILE_DESCRIPTION_HERE>
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import {
    regexps,
    Bom,
    Membrane,
    Trim,
    Corner,
    Tape,
    Adhesive,
    MEMBRANE_TYPES,
    EDGE_TYPES,
    TAPE_TYPES,
    ADHESIVE_TYPES,
    MembraneType,
    EdgeType,
    TapeType,
    AdhesiveType,
    ExtraType,
    Extra,
    EXTRA_TYPES,
    PRODUCT_DISPLAY_CATEGORIES
} from "../../../../package/index.js"

// @@imports-types
import { CategorizedBom } from "../../types/CategorizedBom.js" // Avoiding import conflict on build /quote page...

// @@body
/**
 * 
 * @param {Bom} bom
 * @returns {CategorizedBom[]} 
 */
const getCategorizedBoms = bom => {
    // 
    const onePiece = getMembraneBom(bom, MEMBRANE_TYPES.onePiece)
    const fleeceBacked = getMembraneBom(bom, MEMBRANE_TYPES.fleeceBacked)
    const membranes = {
        title: "Membranes",
        entries: [
            { id: "one-piece", bom: onePiece },
            { id: "fleece-backed", bom: fleeceBacked }
        ]
    }

    // 
    const raisedKerb = getPvcTrimBom(bom, EDGE_TYPES.raisedKerb)
    const gutterDrip = getPvcTrimBom(bom, EDGE_TYPES.gutterDrip)
    const raisedKerbInt = getPvcCornerBom(bom, EDGE_TYPES.raisedKerb, "INT")
    const raisedKerbExt = getPvcCornerBom(bom, EDGE_TYPES.raisedKerb, "EXT")
    const gutterDripInt = getPvcCornerBom(bom, EDGE_TYPES.gutterDrip, "INT")
    const gutterDripExt = getPvcCornerBom(bom, EDGE_TYPES.gutterDrip, "EXT")
    const pvcTrims = {
        title: "PVC Trims",
        entries: [
            { id: "raised-kerb", bom: raisedKerb },
            { id: "gutter-drip", bom: gutterDrip },
            { id: "raised-kerb-internal", bom: raisedKerbInt },
            { id: "raised-kerb-external", bom: raisedKerbExt },
            { id: "gutter-drip-internal", bom: gutterDripInt },
            { id: "gutter-drip-external", bom: gutterDripExt }
        ]
    }

    // 
    const wallTrim = getMetalTrimBom(bom, EDGE_TYPES.houseWall)
    const terminationBar = getMetalTrimBom(bom, EDGE_TYPES.feltJoin)
    const metalTrims = {
        title: "Metal Trims",
        entries: [
            { id: "wall-trim", bom: wallTrim },
            { id: "termination-bar", bom: terminationBar }
        ]
    }

    // 
    const seam = getTapeBom(bom, TAPE_TYPES.seam)
    const cured = getTapeBom(bom, TAPE_TYPES.cured)
    const uncured = getTapeBom(bom, TAPE_TYPES.uncured)
    const uncuredPatch = getTapeBom(bom, TAPE_TYPES.uncured, { isPatch: true })
    const mechanical = getTapeBom(bom, TAPE_TYPES.mechanical)
    const tapes = {
        title: "Tapes",
        entries: [
            { id: "seam-tape", bom: seam },
            { id: "cured-tape", bom: cured },
            { id: "uncured-tape", bom: uncured },
            { id: "uncured-patch", bom: uncuredPatch },
            { id: "mechanical-tape", bom: mechanical }
        ]
    }

    // 
    const waterBased = getAdhesiveBom(bom, ADHESIVE_TYPES.deck.waterBased)
    const pu = getAdhesiveBom(bom, ADHESIVE_TYPES.deck.pu)
    const contact = getAdhesiveBom(bom, ADHESIVE_TYPES.deck.contactBonding)
    const deckAdhesives = {
        title: "Deck Adhesives",
        entries: [
            { id: "water-based", bom: waterBased },
            { id: "pu", bom: pu },
            { id: "contact-bonding", bom: contact }
        ]
    }

    // 
    const primer = getAdhesiveBom(bom, ADHESIVE_TYPES.primer)
    const primers = {
        title: "Primers",
        entries: [
            { id: "primer", bom: primer }
        ]
    }

    // 
    const lapEdge = getAdhesiveBom(bom, ADHESIVE_TYPES.sealants.lapEdge)
    const waterBlock = getAdhesiveBom(bom, ADHESIVE_TYPES.sealants.waterBlock)
    const msPolymer = getAdhesiveBom(bom, ADHESIVE_TYPES.sealants.msPolymer)
    const silicone = getAdhesiveBom(bom, ADHESIVE_TYPES.sealants.blackSilicone)
    const sealants = {
        title: "Sealants",
        entries: [
            { id: "lap-edge", bom: lapEdge },
            { id: "water-block", bom: waterBlock },
            { id: "ms-polymer", bom: msPolymer },
            { id: "black-silicone", bom: silicone }
        ]
    }

    const extras = {
        title: "Extras",
        entries: [
            {
                id: "extras-tools",
                bom: bom.filterDisplayCategory(PRODUCT_DISPLAY_CATEGORIES.extra)
            },
            {
                id: "extras-kits",
                bom: Bom.join([
                    bom.filterDisplayCategory(PRODUCT_DISPLAY_CATEGORIES.kit),
                    bom.filterDisplayCategory(PRODUCT_DISPLAY_CATEGORIES.drain)
                ])
            }
        ]
    }

    return [
        membranes,
        pvcTrims,
        metalTrims,
        tapes,
        deckAdhesives,
        primers,
        sealants,
        extras
    ]
}

// const getOrderedLineItems = bom => {
//     const categorizedBom = getCategorizedBoms(bom)
//     const orderedBom = 
// }

/**
 * 
 * @param {Bom} bom
 * @param {MembraneType} type
 * @returns {Bom}
 */
const getMembraneBom = (bom, type) => {
    return Bom.from(bom.lineItems
        .filter(({ product }) => {
            return product instanceof Membrane && product.type === type
        })
        .sort((lineItemA, lineItemB) => {
            const { quantity: quantityA } = lineItemA
            const widthA = /** @type {number} */ (lineItemA.product.width)
            const { quantity: quantityB } = lineItemB
            const widthB = /** @type {number} */ (lineItemB.product.width)

            return widthA - widthB || quantityA - quantityB
        }))
}

/**
 * 
 * @param {Bom} bom 
 * @param {EdgeType} type
 * @returns {Bom}
 */
const getMetalTrimBom = (bom, type) => {
    return Bom.from(bom.lineItems
        .filter(({ product }) => {
            return product instanceof Trim &&
                product.material === "metal" &&
                product.edgeType === type
        }))
}

/**
 * 
 * @param {Bom} bom 
 * @param {EdgeType} type
 * @returns {Bom}
 */
const getPvcTrimBom = (bom, type) => {
    return Bom.from(bom.lineItems
        .filter(({ product }) => {
            return product instanceof Trim && product.edgeType === type
        }))
}

/**
 * 
 * @param {Bom} bom 
 * @param {EdgeType} type
 * @param {"INT"|"EXT"} angle
 * @returns {Bom}
 */
const getPvcCornerBom = (bom, type, angle) => {
    const cornerAngle = angle === "INT" ? 3 * Math.PI / 2
        : angle === "EXT" ? Math.PI / 2
        : NaN

    return Bom.from(bom.lineItems
        .filter(({ product }) => {
            return product instanceof Corner &&
                product.edgeType === type &&
                product.cornerAngle === cornerAngle
        }))
}

/**
 * 
 * @param {Bom} bom 
 * @param {TapeType} type
 * @param {object} obj
 * @param {boolean} [obj.isPatch]
 * @returns {Bom}
 */
const getTapeBom = (bom, type, { isPatch = false } = {}) => {
    return Bom.from(bom.lineItems
        .filter(({ product }) => {
            return product instanceof Tape &&
                product.type === type &&
                !!product.patchType === isPatch
        })
        .sort((lineItemA, lineItemB) => {
            const widthA = /** @type {number} */ (lineItemA.product.width)
            const widthB = /** @type {number} */ (lineItemB.product.width)

            return widthA - widthB
        }))
}

/**
 * 
 * @param {Bom} bom 
 * @param {AdhesiveType} type
 * @returns {Bom}
 */
const getAdhesiveBom = (bom, type) => {
    return Bom.from(bom.lineItems
        .filter(({ product }) => {
            return product instanceof Adhesive && product.type === type
        })
        .sort((lineItemA, lineItemB) => {
            const { product: { quantity: quantityA } } = lineItemA
            const { product: { quantity: quantityB } } = lineItemB

            return quantityB - quantityA
        }))
}

// @@exports
export { getCategorizedBoms }
